import { useCallback, useEffect, useState } from 'react'
import { Product, ProductSize, PromoCode } from '@/network/graphql.g'
import { useOrderDispatch } from '@/providers/order/old/orderDispatchProvider'
import { calculateDiscount } from '@/core/toStringHelper'

type PurchaseInfo = {
  gift: boolean
  size: ProductSize
}

type UseProductDetailOptions = {
  initialSize?: ProductSize
  serverSelectedSize?: ProductSize
  modalMode?: boolean
  onDidTapClose?: () => void
}

type UseProductDetailResult = {
  sizeId: string
  setSizeId: (sizeId: string) => void
  purchaseInfo: PurchaseInfo
  setPurchaseInfo: (
    purchaseInfo: PurchaseInfo | ((prev: PurchaseInfo) => PurchaseInfo)
  ) => void
  selectedPromoCode: PromoCode | null
  hasSelectedSize: boolean
  hasStock: boolean
  hasAnyStock: boolean
  hasAnyNonStock: boolean
  hasAllSizesStock: boolean
  showSizesPicker: boolean
  setShowSizesPicker: (showSizesPicker: boolean) => void
  showAutomaticallyCouponPopup: boolean
  setAutomaticallyCouponPopup: (show: boolean) => void
  getPromoCode: (isStock?: boolean) => PromoCode | null
  getDisplayPromoCodes: () => PromoCode[]
  mainDisplayPrice: { price: number; showFromPrefix: boolean }
  discountBestPrice: number
  isPriceHigherWithPromo: boolean
  onAddToCart: () => Promise<void>
  onDidChooseSize: () => void
}

export const useProductDetail = (
  product: Product,
  {
    initialSize,
    serverSelectedSize,
    modalMode,
    onDidTapClose
  }: UseProductDetailOptions
): UseProductDetailResult => {
  const { sizes, prices, promoCodes } = product

  const [showSizesPicker, setShowSizesPicker] = useState<boolean>(false)
  const { addToCart: add } = useOrderDispatch()
  const addToCart = (purchaseInfo: PurchaseInfo) =>
    add(product, purchaseInfo.size, purchaseInfo.gift, true, false)

  const getPromoCode = (isStock?: boolean) => {
    if (!promoCodes?.length) {
      return null
    }

    if (promoCodes.length === 1) {
      return promoCodes[0]
    }

    const sortedCodes = [...promoCodes].sort(
      (a, b) => (b.discountValue || 0) - (a.discountValue || 0)
    )

    if (isStock) {
      return sortedCodes[0]
    }

    return sortedCodes[sortedCodes.length - 1]
  }

  const initialSelectedSize =
    serverSelectedSize ??
    initialSize ??
    (sizes && sizes.length === 1 ? sizes[0] : null)

  const [sizeId, setSizeId] = useState<string>(
    () => initialSelectedSize?.id ?? null
  )

  const [showAutomaticallyCouponPopup, setAutomaticallyCouponPopup] =
    useState<boolean>(false)

  const [purchaseInfo, setPurchaseInfo] = useState<PurchaseInfo>({
    size: initialSelectedSize,
    gift: false
  })

  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(
    () =>
      initialSelectedSize ? getPromoCode(initialSelectedSize.isStock) : null
  )

  useEffect(() => {
    if (purchaseInfo.size) {
      setSelectedPromoCode(getPromoCode(purchaseInfo.size.isStock))
    } else {
      setSelectedPromoCode(null)
    }
  }, [purchaseInfo.size])

  const onAddToCart = useCallback(async () => {
    if (purchaseInfo?.size) {
      setAutomaticallyCouponPopup(await addToCart(purchaseInfo))
      modalMode && onDidTapClose()
      return
    }
    setShowSizesPicker(true)
  }, [purchaseInfo])

  const onDidChooseSize = () => {
    setShowSizesPicker(false)
    onAddToCart()
  }

  const hasPromoPrice = (prices?.promoCodePrice || 0) > 0

  const discountBestPrice = hasPromoPrice
    ? calculateDiscount(prices?.promoCodePrice, prices?.minLastMonthPrice)
    : calculateDiscount(prices?.finalPrice, prices?.minLastMonthPrice)

  const isPriceHigherWithPromo =
    product?.prices?.minLastMonthPrice < product?.prices?.finalPrice

  const hasAnyStock = sizes.some((item) => item.isStock === true)
  const hasAnyNonStock = sizes.some((item) => item.isStock === false)
  const hasStock = hasAnyStock
  const hasAllSizesStock = sizes.every((item) => item.isStock === true)

  const getDisplayPromoCodes = () => {
    if (!promoCodes?.length) {
      return []
    }

    if (hasAnyStock && hasAnyNonStock) {
      const stockCode = getPromoCode(true)
      const nonStockCode = getPromoCode(false)
      const result = []
      if (stockCode) {
        result.push(stockCode)
      }
      if (nonStockCode && nonStockCode.id !== stockCode?.id) {
        result.push(nonStockCode)
      }
      return result
    }

    if (purchaseInfo.size) {
      const code = getPromoCode(purchaseInfo.size.isStock)
      return code ? [code] : []
    }

    const code = getPromoCode(hasAnyStock)
    return code ? [code] : []
  }

  const hasSelectedSize = !!purchaseInfo.size

  const mainDisplayPrice = (() => {
    if (hasSelectedSize) {
      return {
        price:
          selectedPromoCode?.discountAmount ??
          prices?.promoCodePrice ??
          prices?.finalPrice,
        showFromPrefix: false
      }
    }
    const displayCodes = getDisplayPromoCodes()
    const promoCodesCount = displayCodes.length
    return {
      price:
        promoCodesCount >= 2
          ? Math.min(...displayCodes.map((c) => c?.discountAmount || 0))
          : prices?.promoCodePrice ?? prices?.finalPrice,
      showFromPrefix: promoCodesCount >= 2
    }
  })()

  return {
    sizeId,
    setSizeId,
    purchaseInfo,
    setPurchaseInfo,
    selectedPromoCode,
    hasSelectedSize,
    hasStock,
    hasAnyStock,
    hasAnyNonStock,
    hasAllSizesStock,
    showSizesPicker,
    setShowSizesPicker,
    showAutomaticallyCouponPopup,
    setAutomaticallyCouponPopup,
    getPromoCode,
    getDisplayPromoCodes,
    mainDisplayPrice,
    discountBestPrice,
    isPriceHigherWithPromo,
    onAddToCart,
    onDidChooseSize
  }
}
